import { useQuery } from '@apollo/client';
import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { CompanyQuery } from 'components/views/_shared-queries/company.graphql';
import {
  ConfirmModal,
  ConfirmModalActions,
  ConfirmModalBody,
  ConfirmModalTitle,
} from 'lead-list/components/ConfirmModal';
import { LeadListTypeIcon } from 'lead-list/components/LeadListTypeIcon';
import { Paper } from 'lead-list/components/Paper';
import { useStateMachine } from 'little-state-machine';
import { useAuthContext } from 'providers/auth';
import { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, useNavigate, useParams } from 'react-router';
import routes from 'routes';
import { ListType } from 'types/apollo/globalTypes';
import { STEPS } from '../../LeadListCreate';
import { clearLeadListCreate, setLeadListCreate } from '../../state/actions';
import { LeadListReview } from '../LeadListReview';
import { Nav } from '../Nav';
import { StepContainer } from '../StepContainer';
import { FilePreviewDialog } from './FilePreviewDialog';
import messages from './messages';

const joinUrl = (...parts: string[]) => parts.join('/').replace(/\/+/g, '/');

export const StepReview: FC = () => {
  const navigate = useNavigate();
  const { id: companyIdParam } = useParams();
  const { isInternal } = useAuthContext();
  const {
    state: { leadListCreate },
    actions,
  } = useStateMachine({ setLeadListCreate, clearLeadListCreate });

  const { formatMessage } = useIntl();
  const [goGreen, setGoGreen] = useState(false);
  const [_goBack, setGoBack] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const shouldUseCompanyFromRoute = isInternal;
  const companyId = useMemo(
    () => (shouldUseCompanyFromRoute && companyIdParam ? Number(companyIdParam) : undefined),
    [shouldUseCompanyFromRoute, companyIdParam],
  );

  const {
    data: companyData,
    loading: companyLoading,
    error: companyError,
  } = useQuery(CompanyQuery, {
    variables: { companyId: companyId as number },
    skip: !shouldUseCompanyFromRoute || !companyId,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!shouldUseCompanyFromRoute) return;
    if (companyData?.company && !leadListCreate.company?.id) {
      actions.setLeadListCreate({
        company: {
          id: String(companyData.company.id),
          name: companyData.company.name,
          __typename: 'Company',
        },
      });
    }
  }, [shouldUseCompanyFromRoute, companyData, leadListCreate.company?.id, actions]);

  if (
    !leadListCreate.file ||
    !leadListCreate.listType ||
    (leadListCreate.listType === ListType.PROSPECTIVE && !leadListCreate.campaign) ||
    !leadListCreate.mappings ||
    !leadListCreate.estimatedTotalRows
  ) {
    return <Navigate to=".." replace relative="path" />;
  }

  if (shouldUseCompanyFromRoute && companyLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  const adminImportCreate = companyIdParam
    ? joinUrl(
        routes.company.resolve({ id: companyIdParam }),
        routes.company.leads.path,
        routes.company.leads.importCreate.path,
      )
    : routes.leads.importCreate.path;

  const adminImportCreateAbs = adminImportCreate.startsWith('/')
    ? adminImportCreate
    : `/${adminImportCreate}`;

  if (shouldUseCompanyFromRoute && (companyError || !companyData?.company)) {
    return <Navigate to={adminImportCreateAbs} replace />;
  }

  const nextStep = () => {
    if (goGreen) return;
    setShowConfirmation(false);
    setGoGreen(true);
    setTimeout(() => navigate(`../${STEPS.importing}`), 500);
  };

  const onImportClick = () => {
    if (leadListCreate.listType === ListType.PROSPECTIVE) {
      nextStep();
    } else {
      setShowConfirmation(true);
    }
  };

  const confirmTypeMap: Record<ListType, string> = {
    [ListType.DNC]: 'DNC',
    [ListType.CLOSE]: 'Closed',
    [ListType.ENDCONTACT]: 'End Contact',
    [ListType.PROSPECTIVE]: '',
  };
  const confirmType = confirmTypeMap[leadListCreate.listType];

  const companyForReview = (shouldUseCompanyFromRoute &&
    companyData?.company && {
      __typename: 'Company',
      id: String(companyData.company.id),
      name: companyData.company.name,
    }) ||
    (leadListCreate.company && {
      __typename: 'Company',
      id: String(leadListCreate.company.id),
      name: leadListCreate.company.name,
    }) || { __typename: 'Company', id: '', name: '' };

  return (
    <StepContainer>
      <h1>{formatMessage(messages.reviewTitle)}</h1>

      <Paper
        variant="outlined"
        sx={(theme) => ({
          width: '100%',
          padding: theme.spacing(3),
          marginBottom: theme.spacing(4),
        })}
        shade={goGreen ? 'success' : undefined}
      >
        <LeadListReview
          leads={leadListCreate.estimatedTotalRows}
          fileName={leadListCreate.file.name}
          listType={leadListCreate.listType}
          company={companyForReview}
          campaign={leadListCreate.campaign?.name || ''}
        />
      </Paper>

      <Box mt={4} width="50%" mx="auto" display="flex" gap={2}>
        <Button variant="outlined" color="primary" fullWidth onClick={() => setShowPreview(true)}>
          Preview
        </Button>
        <Button variant="contained" color="primary" fullWidth onClick={onImportClick}>
          {formatMessage(messages.reviewImportList)}
        </Button>
      </Box>

      {leadListCreate.fileHeaders && (
        <FilePreviewDialog
          open={showPreview}
          onClose={() => setShowPreview(false)}
          file={leadListCreate.file}
          fileHeaders={leadListCreate.fileHeaders}
          mappings={leadListCreate.mappings}
        />
      )}

      <Nav
        onBackClick={() => {
          setGoBack(true);
          navigate(-1);
        }}
      />

      <ConfirmModal open={!!showConfirmation} onClose={() => setShowConfirmation(false)}>
        <ConfirmModalTitle>{`Ready to mark leads as '${confirmType}'`}</ConfirmModalTitle>
        <LeadListTypeIcon
          listType={leadListCreate.listType}
          sx={{ position: 'absolute', top: '16px', right: '16px', fontSize: '28px' }}
        />
        <ConfirmModalBody>
          We&apos;ll make sure not to send any messages to these contacts. You won&apos;t be able to
          undo this action.
        </ConfirmModalBody>
        <ConfirmModalActions onClose={() => setShowConfirmation(false)} onConfirm={nextStep} />
      </ConfirmModal>
    </StepContainer>
  );
};
